import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    Button,
    CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAxiosPrivate from "utils/hooks/useAxiosPrivate";
import ModalCloseButton from "../../components/UI/ModalCloseButton";
import styles from "./report.module.css";
import {
    modalBackdropProps,
    MODAL_PAPER_SX,
} from "../../utils/styles/modal.overlay";

// Yup validation schema
const interpretationSchema = Yup.object().shape({
    interpretation: Yup.string()
        .trim()
        .required("Interpretation is required")
        .max(1000, "Interpretation must be at most 1000 characters"),
});

function ReviewModal({ selectedReport, open, onClose, onSubmit, tabValue }) {
    const axios = useAxiosPrivate();
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(false);

    useEffect(() => {
        let objectUrl = null;

        const fetchPdf = async () => {
            const pdfUrl = selectedReport?.report_pdf;
            if (!open || !pdfUrl) {
                setPdfBlobUrl(null);
                return;
            }

            setPdfLoading(true);
            setPdfError(false);

            try {
                const { data } = await axios.get(pdfUrl, {
                    responseType: "blob",
                });
                objectUrl = URL.createObjectURL(
                    new Blob([data], { type: "application/pdf" }),
                );
                setPdfBlobUrl(objectUrl);
            } catch {
                setPdfError(true);
            } finally {
                setPdfLoading(false);
            }
        };

        fetchPdf();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setPdfBlobUrl(null);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedReport?.report_pdf]);

    const formik = useFormik({
        initialValues: {
            interpretation: "",
        },
        validationSchema: interpretationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                await onSubmit(values.interpretation);
                resetForm();
                onClose();
            } catch (error) {
                console.error("Submit failed:", error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const showError =
        formik.touched.interpretation && Boolean(formik.errors.interpretation);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            BackdropProps={modalBackdropProps}
            PaperProps={{
                sx: {
                    ...MODAL_PAPER_SX,
                    borderRadius: "16px",
                    width: "1000px",
                    maxWidth: "calc(100vw - 48px)",
                    maxHeight: "calc(100vh - 64px)",
                    height: "calc(100vh - 64px)",
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            <ModalCloseButton onClick={handleClose} />

            <DialogContent
                style={{
                    marginTop: "15px",
                    padding: "0 0 24px 0",
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        marginTop: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {pdfLoading && (
                        <CircularProgress
                            style={{ color: "var(--primary-theme-color)" }}
                        />
                    )}
                    {pdfError && (
                        <Typography
                            style={{
                                fontFamily: "Poppins",
                                fontSize: "14px",
                                color: "#d32f2f",
                            }}
                        >
                            Failed to load PDF.
                        </Typography>
                    )}
                    {pdfBlobUrl && !pdfLoading && (
                        <iframe
                            src={pdfBlobUrl}
                            title="Report PDF"
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                display: "block",
                            }}
                        />
                    )}
                </div>

                {tabValue === 1 && (
                    <>
                        <Typography
                            style={{
                                fontFamily: "Poppins",
                                fontSize: "18px",
                                fontWeight: 500,
                                color: "var(--primary-text-color)",
                                marginBottom: "24px",
                                marginTop: "12px",
                            }}
                        >
                            Team Staff Interpretation
                        </Typography>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ position: "relative" }}>
                                <textarea
                                    name="interpretation"
                                    value={formik.values.interpretation}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter your interpretation here..."
                                    className={
                                        showError
                                            ? styles.interpretationTextareaError
                                            : styles.interpretationTextarea
                                    }
                                    style={{
                                        width: "97%",
                                        minHeight: "120px",
                                        padding: "12px",
                                        borderRadius: "4px",
                                        border: `1px solid ${showError ? "#d32f2f" : "var(--primary-popup-stroke-color)"}`,
                                        backgroundColor:
                                            "var(--primary-popup-background-color)",
                                        color: "var(--primary-text-color)",
                                        fontFamily: "Poppins",
                                        fontSize: "14px",
                                        resize: "vertical",
                                    }}
                                />
                                {showError && (
                                    <Typography
                                        style={{
                                            color: "#d32f2f",
                                            fontSize: "12px",
                                            marginTop: "4px",
                                            fontFamily: "Poppins",
                                        }}
                                    >
                                        {formik.errors.interpretation}
                                    </Typography>
                                )}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: "24px",
                                    gap: "12px",
                                }}
                            >
                                <Button
                                    type="submit"
                                    disabled={
                                        !formik.values.interpretation.trim() ||
                                        formik.isSubmitting
                                    }
                                    style={{
                                        padding: "4px 32px",
                                        backgroundColor:
                                            "var(--primary-theme-color)",
                                        color: "var(--primary-button-text-color)",
                                        borderRadius: "4px",
                                        textTransform: "none",
                                        fontFamily: "Poppins",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        opacity:
                                            !formik.values.interpretation.trim() ||
                                            formik.isSubmitting
                                                ? 0.7
                                                : 1,
                                        cursor:
                                            !formik.values.interpretation.trim() ||
                                            formik.isSubmitting
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    {formik.isSubmitting
                                        ? "Submitting..."
                                        : "Sign & Save"}
                                </Button>
                            </div>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ReviewModal;
